/**
 * Exchange rates — PURE. No React, no I/O.
 *
 * "Every model has its own rate into Ruby Tokens" (mockup board 1, Task 15):
 * for each priced model, the vendor's own billing unit (token / image /
 * second / call), what it costs us, what that is in Ruby Tokens at cost
 * (1 Ruby Token = $0.00001, spec §3), and what a tenant is charged for one
 * unit once the multiplier is applied. The multiplier shown is the one on
 * the action that uses this model as its PRIMARY model (not a fallback) —
 * absent any such action, the UI shows the 1.5 default, greyed.
 *
 * Named `exchangeRateMath.ts`, not `exchangeRates.ts` as the task brief
 * literally says: a same-directory `exchangeRates.ts` / `ExchangeRates.tsx`
 * pair (case-only difference) made Vite's module resolver hand the
 * component's default import the wrong module (resolved to this file,
 * `undefined` default) — reproduced in `ProvidersModelsTab.test.tsx`. This
 * name sidesteps the collision; `ExchangeRates.tsx` imports from here.
 */
import type { AiModelDto, AiUsageCountsDto } from '../../../services/aiControl/registry';
import type { AiActionDto, AiVibeDto } from '../../../services/aiControl/rubyAi';
import { estimateCharge, vendorPriceLabel } from './catalogModel';
import { liveTokensPerUsd, liveDefaultMultiplier } from './units';

/**
 * DEFAULTS only — 1 Ruby Token = $0.00001 → $1 = 100,000 Ruby Tokens, and a
 * 1.5 default multiplier. The live values (AI → Settings) are read through
 * liveTokensPerUsd() / liveDefaultMultiplier().
 */
export const RUBY_TOKENS_PER_USD = 100_000;

/** A model with no primary action gets the live default multiplier (1.5 unless changed), shown greyed. */
export const DEFAULT_MULTIPLIER = 1.5;

export interface RateRow {
  providerId: string;
  modelId: string;
  label: string;
  unit: string;
  vendorPriceText: string;
  rubyPerUnitAtCost: number;
  multiplier: number;
  rubyPerUnitCharged: number;
  typical?: { qty: number; unitsLabel: string; costUsd: number; billedTokens: number };
  /** True when `multiplier` is the 1.5 fallback, not a real action's own multiplier. */
  isDefaultMultiplier?: boolean;
}

interface PrimaryUnit { unit: string; unitPriceUsd: number }

/** The one unit the vendor actually bills this model on, and its per-unit USD price. */
function primaryUnit(unitPrices: AiModelDto['unitPrices'] | undefined): PrimaryUnit | null {
  const p = unitPrices ?? {};
  if (typeof p.per_image_usd === 'number' && p.per_image_usd > 0) return { unit: 'image', unitPriceUsd: p.per_image_usd };
  if (typeof p.per_call_usd === 'number' && p.per_call_usd > 0) return { unit: 'call', unitPriceUsd: p.per_call_usd };
  if (typeof p.per_second_usd === 'number' && p.per_second_usd > 0) return { unit: 'second', unitPriceUsd: p.per_second_usd };
  const perMillion = typeof p.output_per_million_usd === 'number' && p.output_per_million_usd > 0
    ? p.output_per_million_usd
    : (typeof p.input_per_million_usd === 'number' && p.input_per_million_usd > 0 ? p.input_per_million_usd : undefined);
  if (typeof perMillion === 'number') return { unit: 'token', unitPriceUsd: perMillion / 1_000_000 };
  return null;
}

/** Round to 2dp — clears IEEE-754 noise without hiding a real fraction (e.g. 4.5). */
const round2 = (n: number): number => Math.round(n * 100) / 100;

function primaryMultiplier(providerId: string, modelId: string, actions: AiActionDto[]): { multiplier: number; isDefault: boolean } {
  const match = actions.find((a) => a.providerId === providerId && a.modelId === modelId)
    ?? actions.find((a) => (a.steps ?? []).some((s) => s.providerId === providerId && s.modelId === modelId));
  if (match && typeof match.multiplier === 'number') return { multiplier: match.multiplier, isDefault: false };
  return { multiplier: liveDefaultMultiplier(), isDefault: true };
}

function typicalFor(unit: string, m: AiModelDto, tu: AiUsageCountsDto, multiplier: number): RateRow['typical'] {
  let qty = 0;
  let unitsLabel = '';
  let costUsd = 0;
  if (unit === 'token') {
    qty = tu.outputTokens ?? 0;
    unitsLabel = qty === 1 ? 'output token' : 'output tokens';
    costUsd = ((tu.inputTokens ?? 0) * (m.unitPrices?.input_per_million_usd ?? 0)
      + (tu.outputTokens ?? 0) * (m.unitPrices?.output_per_million_usd ?? 0)) / 1_000_000;
  } else if (unit === 'image') {
    qty = tu.images ?? 1;
    unitsLabel = qty === 1 ? 'image' : 'images';
    costUsd = qty * (m.unitPrices?.per_image_usd ?? 0);
  } else if (unit === 'second') {
    qty = tu.seconds ?? 1;
    unitsLabel = qty === 1 ? 'second' : 'seconds';
    costUsd = qty * (m.unitPrices?.per_second_usd ?? 0);
  } else {
    qty = tu.calls ?? 1;
    unitsLabel = qty === 1 ? 'call' : 'calls';
    costUsd = qty * (m.unitPrices?.per_call_usd ?? 0);
  }
  if (qty <= 0) return undefined;
  // Same formula as a real charge (spec §3) — whole credits, ×1,000 Ruby Tokens.
  const { rubyTokens } = estimateCharge({ vendorCostUsd: costUsd, multiplier, minTokens: 0, maxTokens: Number.MAX_SAFE_INTEGER });
  return { qty, unitsLabel, costUsd, billedTokens: rubyTokens };
}

/**
 * One row per priced model — unpriced models have no rate to show. `vibes`
 * is accepted for forward compatibility (a vibe's own multiplier override on
 * this model may one day take precedence) but is not consulted today: the
 * action's own multiplier is the rate shown.
 */
export function exchangeRows(models: AiModelDto[], actions: AiActionDto[], _vibes: AiVibeDto[]): RateRow[] {
  const rows: RateRow[] = [];
  for (const m of models) {
    const unitInfo = primaryUnit(m.unitPrices);
    if (!unitInfo) continue;
    const rubyPerUnitAtCost = round2(unitInfo.unitPriceUsd * liveTokensPerUsd());
    const { multiplier, isDefault } = primaryMultiplier(m.providerId, m.modelId, actions);
    const rubyPerUnitCharged = round2(rubyPerUnitAtCost * multiplier);
    const typical = m.typicalUsage ? typicalFor(unitInfo.unit, m, m.typicalUsage, multiplier) : undefined;
    rows.push({
      providerId: m.providerId,
      modelId: m.modelId,
      label: m.label,
      unit: unitInfo.unit,
      vendorPriceText: vendorPriceLabel(m.unitPrices),
      rubyPerUnitAtCost,
      multiplier,
      rubyPerUnitCharged,
      typical,
      isDefaultMultiplier: isDefault,
    });
  }
  return rows;
}
